import { defineMessages, type IntlShape, type MessageDescriptor } from "react-intl";

import {
  ON_DEMAND_CAMPAIGN_MAX_ITEMS,
  type OnDemandCampaignCreateRequestV1,
  type OnDemandCampaignGenerationPostType,
} from "@runcheetah/shared";

const onDemandCampaignFormatMessages = defineMessages({
  portraitLabel: {
    id: "onDemandCampaign.format.portrait.label",
    defaultMessage: "Portrait post",
  },
  portraitDescription: {
    id: "onDemandCampaign.format.portrait.description",
    defaultMessage: "Primary Instagram feed creative, 4:5.",
  },
  squareLabel: {
    id: "onDemandCampaign.format.square.label",
    defaultMessage: "Square post",
  },
  squareDescription: {
    id: "onDemandCampaign.format.square.description",
    defaultMessage: "Compact proof or product moment, 1:1.",
  },
  storyLabel: {
    id: "onDemandCampaign.format.story.label",
    defaultMessage: "Instagram Story",
  },
  storyDescription: {
    id: "onDemandCampaign.format.story.description",
    defaultMessage: "Static Instagram campaign creative, 9:16.",
  },
  carouselPortraitLabel: {
    id: "onDemandCampaign.format.carouselPortrait.label",
    defaultMessage: "Portrait carousel",
  },
  carouselPortraitDescription: {
    id: "onDemandCampaign.format.carouselPortrait.description",
    defaultMessage: "Multi-slide Instagram feed sequence, 4:5.",
  },
  carouselSquareLabel: {
    id: "onDemandCampaign.format.carouselSquare.label",
    defaultMessage: "Square carousel",
  },
  carouselSquareDescription: {
    id: "onDemandCampaign.format.carouselSquare.description",
    defaultMessage: "Multi-slide Instagram feed sequence, 1:1.",
  },
});

export const ON_DEMAND_CAMPAIGN_FORMATS: Array<{
  postType: OnDemandCampaignGenerationPostType;
  label: MessageDescriptor;
  description: MessageDescriptor;
}> = [
  {
    postType: "feed_portrait",
    label: onDemandCampaignFormatMessages.portraitLabel,
    description: onDemandCampaignFormatMessages.portraitDescription,
  },
  {
    postType: "feed_square",
    label: onDemandCampaignFormatMessages.squareLabel,
    description: onDemandCampaignFormatMessages.squareDescription,
  },
  {
    postType: "story",
    label: onDemandCampaignFormatMessages.storyLabel,
    description: onDemandCampaignFormatMessages.storyDescription,
  },
  {
    postType: "carousel_portrait",
    label: onDemandCampaignFormatMessages.carouselPortraitLabel,
    description: onDemandCampaignFormatMessages.carouselPortraitDescription,
  },
  {
    postType: "carousel_square",
    label: onDemandCampaignFormatMessages.carouselSquareLabel,
    description: onDemandCampaignFormatMessages.carouselSquareDescription,
  },
];

const suggestedCampaignPromptMessages = defineMessages({
  fallbackBrandName: {
    id: "onDemandCampaign.suggestedPrompt.fallbackBrandName",
    defaultMessage: "this brand",
  },
  prompt: {
    id: "onDemandCampaign.suggestedPrompt.prompt",
    defaultMessage:
      'Goal: drive consideration for {brandName} among high-intent buyers. Mix one strong "why we exist" piece with proof-led product education and a customer moment. Tone should land as sharp, practical, and opinionated.',
  },
});

export type OnDemandCampaignPostType = (typeof ON_DEMAND_CAMPAIGN_FORMATS)[number]["postType"];
export type OnDemandCampaignCounts = Record<OnDemandCampaignPostType, number>;

export const createDefaultOnDemandCampaignCounts = (): OnDemandCampaignCounts => ({
  feed_portrait: 4,
  feed_square: 1,
  story: 0,
  carousel_portrait: 0,
  carousel_square: 0,
});

export const getOnDemandCampaignCountTotal = (counts: OnDemandCampaignCounts): number =>
  Object.values(counts).reduce((sum, value) => sum + value, 0);

export const updateOnDemandCampaignCount = (input: {
  counts: OnDemandCampaignCounts;
  postType: OnDemandCampaignPostType;
  delta: number;
  maxTotal?: number;
}): OnDemandCampaignCounts => {
  const current = input.counts[input.postType];
  const nextValue = Math.max(0, current + input.delta);
  const nextCounts = { ...input.counts, [input.postType]: nextValue };
  const total = getOnDemandCampaignCountTotal(nextCounts);

  if (total > (input.maxTotal ?? ON_DEMAND_CAMPAIGN_MAX_ITEMS)) {
    return input.counts;
  }

  return nextCounts;
};

export const buildSuggestedCampaignPrompt = (
  intl: IntlShape,
  brandName?: string | null,
): string => {
  const name =
    brandName?.trim() || intl.formatMessage(suggestedCampaignPromptMessages.fallbackBrandName);

  return intl.formatMessage(suggestedCampaignPromptMessages.prompt, { brandName: name });
};

export const buildOnDemandCampaignCreateRequest = (input: {
  prompt: string;
  counts: OnDemandCampaignCounts;
  firstScheduledFor: string;
  intervalHours: number;
  generationMode?: OnDemandCampaignCreateRequestV1["generationMode"];
}): OnDemandCampaignCreateRequestV1 => {
  const firstDate = new Date(input.firstScheduledFor);
  const intervalMs = input.intervalHours * 60 * 60 * 1000;
  const items = ON_DEMAND_CAMPAIGN_FORMATS.flatMap((format) =>
    Array.from({ length: input.counts[format.postType] }, () => ({
      platform: "instagram" as const,
      postType: format.postType,
    })),
  ).map((item, index) => ({
    ...item,
    scheduledFor: new Date(firstDate.getTime() + intervalMs * index).toISOString(),
  }));

  return {
    version: 1,
    prompt: input.prompt.trim(),
    items,
    generationMode: input.generationMode ?? "preview_first",
  };
};
